import { useEffect, useMemo, useState } from "react";
import type { PartnerProgramMembership, PayoutMethodSnapshot } from "../../shared/types";
import { partnerApi, PartnerApiError } from "../lib/api";

/**
 * Edit how the partner gets paid (node P5), reusing P1's REAL onboarding endpoint
 * (partnerApi.savePayoutMethod -> PUT /api/partner/payout-method). It records the
 * method + details the partner supplies; MONEY RULE, Flashadz never moves this
 * money, the owner pays directly. No mock: the same server path onboarding uses.
 */

export type PayKind = "paypal" | "wise" | "bank" | "other";

const KIND_LABEL: Record<PayKind, string> = {
  paypal: "PayPal",
  wise: "Wise",
  bank: "Bank transfer",
  other: "Other",
};

/** Recover the editing kind from a saved method label. */
function kindFromMethod(method: string | undefined): PayKind {
  const m = (method ?? "").toLowerCase();
  if (m.includes("paypal")) return "paypal";
  if (m.includes("wise")) return "wise";
  if (m.includes("bank")) return "bank";
  return m === "" ? "paypal" : "other";
}

/**
 * The payout method being edited: the kind, each field, and a save to the real endpoint. Shared by the
 * computer's dialog and the phone's sheet.
 */
export function usePayoutMethodForm(
  programId: string,
  current: PayoutMethodSnapshot | null,
  onSaved: (membership: PartnerProgramMembership) => void,
) {
  const [kind, setKind] = useState<PayKind>(() => kindFromMethod(current?.method));

  // Prefill from the saved method when it matches the selected kind.
  const initial = useMemo(() => current ?? {}, [current]);
  const [email, setEmail] = useState(initial.email ?? "");
  const [holder, setHolder] = useState(initial.holder ?? "");
  const [bank, setBank] = useState(initial.bank ?? "");
  const [account, setAccount] = useState(initial.accountNo ?? initial.account ?? "");
  const [routing, setRouting] = useState(initial.routing ?? "");
  const [otherName, setOtherName] = useState(
    current && kindFromMethod(current.method) === "other" ? (current.method ?? "") : "",
  );
  const [otherHow, setOtherHow] = useState(initial.how ?? initial.details ?? "");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function canSave(): boolean {
    if (busy) return false;
    if (kind === "paypal" || kind === "wise") return email.trim().length > 0;
    if (kind === "bank") return account.trim().length > 0;
    return otherHow.trim().length > 0;
  }

  async function save() {
    if (!canSave()) return;
    setBusy(true);
    setError(null);
    let method = KIND_LABEL[kind];
    let fields: Record<string, string> = {};
    if (kind === "paypal" || kind === "wise") {
      fields = { email: email.trim() };
    } else if (kind === "bank") {
      fields = {
        holder: holder.trim(),
        bank: bank.trim(),
        accountNo: account.trim(),
        routing: routing.trim(),
      };
    } else {
      method = otherName.trim() || "Other";
      fields = { how: otherHow.trim() };
    }
    try {
      const { membership } = await partnerApi.savePayoutMethod({ programId, method, fields });
      onSaved(membership);
    } catch (err) {
      setError(err instanceof PartnerApiError ? err.message : "Could not save your payout method.");
      setBusy(false);
    }
  }

  return {
    kind,
    setKind,
    email,
    setEmail,
    holder,
    setHolder,
    bank,
    setBank,
    account,
    setAccount,
    routing,
    setRouting,
    otherName,
    setOtherName,
    otherHow,
    setOtherHow,
    busy,
    error,
    canSave,
    save,
  };
}

export function PayoutMethodModal({
  programId,
  current,
  onClose,
  onSaved,
}: {
  programId: string;
  current: PayoutMethodSnapshot | null;
  onClose: () => void;
  onSaved: (membership: PartnerProgramMembership) => void;
}) {
  const {
    kind,
    setKind,
    email,
    setEmail,
    holder,
    setHolder,
    bank,
    setBank,
    account,
    setAccount,
    routing,
    setRouting,
    otherName,
    setOtherName,
    otherHow,
    setOtherHow,
    busy,
    error,
    canSave,
    save,
  } = usePayoutMethodForm(programId, current, onSaved);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="scrim open"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label="Payout method">
        <div className="mhead">
          <h3>Payout method</h3>
          <button type="button" className="mx" aria-label="Close" onClick={onClose}>
            &#10005;
          </button>
        </div>
        <div className="mbody">
          <div className="pm-hint">
            <b>The owner pays you directly.</b> Flashadz records how you want to be paid, it never
            holds your money.
          </div>
          <label className="pm-flabel">How you want to be paid</label>
          <div className="seg" style={{ marginBottom: 18 }}>
            {(["paypal", "wise", "bank", "other"] as PayKind[]).map((k) => (
              <button
                key={k}
                type="button"
                className={kind === k ? "on" : ""}
                onClick={() => setKind(k)}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>

          {(kind === "paypal" || kind === "wise") && (
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor="pm-email">
                {kind === "paypal" ? "PayPal email" : "Wise email or Wisetag"}
              </label>
              <input
                id="pm-email"
                className="inp"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@email.com"
              />
            </div>
          )}

          {kind === "bank" && (
            <>
              <div className="frow">
                <div className="field">
                  <label htmlFor="pm-holder">Account holder name</label>
                  <input
                    id="pm-holder"
                    className="inp"
                    value={holder}
                    onChange={(e) => setHolder(e.target.value)}
                    placeholder="As it appears on the account"
                  />
                </div>
                <div className="field">
                  <label htmlFor="pm-bank">Bank name</label>
                  <input
                    id="pm-bank"
                    className="inp"
                    value={bank}
                    onChange={(e) => setBank(e.target.value)}
                    placeholder="e.g. Chase"
                  />
                </div>
              </div>
              <div className="frow">
                <div className="field">
                  <label htmlFor="pm-account">Account number or IBAN</label>
                  <input
                    id="pm-account"
                    className="inp"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    placeholder="Account number or IBAN"
                  />
                </div>
                <div className="field">
                  <label htmlFor="pm-routing">Routing or SWIFT</label>
                  <input
                    id="pm-routing"
                    className="inp"
                    value={routing}
                    onChange={(e) => setRouting(e.target.value)}
                    placeholder="Routing or SWIFT / BIC"
                  />
                </div>
              </div>
            </>
          )}

          {kind === "other" && (
            <>
              <div className="field">
                <label htmlFor="pm-othername">Method name</label>
                <input
                  id="pm-othername"
                  className="inp"
                  value={otherName}
                  onChange={(e) => setOtherName(e.target.value)}
                  placeholder="Venmo, Zelle, Revolut, etc."
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="pm-otherhow">How to pay you</label>
                <textarea
                  id="pm-otherhow"
                  className="inp"
                  rows={3}
                  value={otherHow}
                  onChange={(e) => setOtherHow(e.target.value)}
                  placeholder="Handle, link, or steps to send you money"
                />
              </div>
            </>
          )}

          {error && (
            <div className="ob-error" role="alert" style={{ marginTop: 14 }}>
              {error}
            </div>
          )}
        </div>
        <div className="mfoot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={save} disabled={!canSave()}>
            {busy ? "Saving..." : "Save method"}
          </button>
        </div>
      </div>
    </div>
  );
}
